// Operations' tables and rows (BL-189; were ProfitViews' ProfitDay, JobMargins, ProfitByTech and
// HuddleDays, pages/ux-full screens/ads.js): what each says, in words, with no drawing of its own.
// - A day's gross profit (reports.jobCosting): revenue, its costs and paid labour, the total, its share of
//   revenue against the target margin as a bar, and the jobs whose numbers may still move or have nothing
//   logged.
// - The jobs (reports.jobCosting beside each job's jobs.get): one row a job, the day's total under them;
//   the share and the numbers' state as words (under target and not final dashed under: waiting).
// - By technician: each one's share of the jobs by the paid time they logged, and what they made for each
//   hour beside its bar.
// - The huddle (gp.huddle): the targets the office plans by, and the last working days' revenue as bars,
//   a day the books closed hatched.
import type { DataColumn, DataLine, DataWord } from '../components/DataTable/DataTable.tsx';
import type { KeyValueItem } from '../components/KeyValue/KeyValue.tsx';
import type { Row } from '../components/RowList/RowList.tsx';
import {
  bare,
  byTechnician,
  hoursText,
  margin,
  marginText,
  notFinalWhy,
  SOURCE,
  techsOf,
  underTarget,
  type TechShare,
} from '../components/ProfitViews.tsx';
import { money, plural } from '../format.ts';
import type { CostedJob, CostRow, HuddleDaysProps, JobCosting } from '../schemas.ts';

// ── A day's gross profit ──

/** The day's ledger: revenue, its costs and paid labour (as they come off it), and the total. */
export function profitFacts(p: JobCosting): KeyValueItem[] {
  return [
    { label: 'Revenue, before tax', value: money(p.revenueCents) },
    { label: 'Materials, purchases and receipts', value: money(-p.costOfSalesCents) },
    {
      label: p.labourMinutes ? `Paid labour, ${hoursText(p.labourMinutes)}` : 'Paid labour',
      value: money(-p.labourCents),
    },
    { label: 'Gross profit', value: money(p.grossProfitCents), tone: 'strong', bad: p.grossProfitCents < 0 },
  ];
}

/** Its share of revenue as a bar against the target's mark, and the word when it's under; null with no revenue. */
export function shareRow(p: JobCosting): Row | null {
  const pct = margin(p);
  if (pct === null) return null;
  const target = p.targetMarginPct;
  const under = underTarget(pct, target);
  return {
    id: 'share',
    name: 'Share of revenue',
    bar: {
      value: Math.max(0, pct),
      max: 100,
      label: 'Gross profit, as a share of revenue',
      state: under ? 'low' : 'ok',
      ...(target !== null ? { marks: [{ at: target, label: `The ${target} % target` }] } : {}),
    },
    end: { figure: marginText(pct) },
    ...(target !== null
      ? { lines: [{ text: under ? `Under the ${target} % target` : `The target is ${target} %.` }] }
      : {}),
  };
}

/** The jobs whose numbers may still move, each with why: purchases waiting, hours no pay rate covers. */
export function notFinalRows(p: JobCosting, open: (jobId: string) => void): Row[] {
  return p.rows
    .filter((r) => notFinalWhy(r).length > 0)
    .map((r) => ({
      id: r.jobId,
      name: r.jobNumber,
      onOpen: () => open(r.jobId),
      lines: notFinalWhy(r).map((text) => ({ text })),
    }));
}

/** The jobs with revenue and nothing logged against them. */
export function unloggedRows(p: JobCosting, open: (jobId: string) => void): Row[] {
  return p.rows.filter(bare).map((r) => ({
    id: r.jobId,
    name: r.jobNumber,
    onOpen: () => open(r.jobId),
    lines: [{ text: 'No part, purchase or receipt on it: check nothing was used, or that it was logged' }],
  }));
}

/** What to know about where the day's costs came from: materials at an old cost, a day the books don't hold. */
export function profitNote(p: JobCosting): string | null {
  const untracked = p.rows.reduce((a, r) => a + r.untrackedMaterialsCents, 0);
  const words = [
    untracked > 0 ? `${money(untracked)} of the materials are at the cost they had when used.` : null,
    SOURCE[p.source],
  ].filter(Boolean);
  return words.length ? words.join(' ') : null;
}

// ── The jobs ──

/** One job's row (or the day's total, `total`): its job, who was on it, and its figures. */
export interface JobMarginLine {
  id: string;
  /** The job's number; null for the total. */
  number: string | null;
  customer: string | null;
  summary: string | null;
  /** Who was on it, the lead first; null when its record isn't read. */
  techs: string | null;
  revenueCents: number;
  costOfSalesCents: number;
  labourCents: number;
  labourMinutes: number;
  grossProfitCents: number;
  marginPct: number | null;
  /** Whether its numbers may still move, and whether nothing was logged; the total has neither. */
  notFinal: boolean;
  unlogged: boolean;
  total?: boolean;
}

/** The day's jobs, most gross profit first, beside each job's record, and their total. */
export function jobMarginLines(
  c: JobCosting,
  jobs: readonly CostedJob[],
): { rows: JobMarginLine[]; total: JobMarginLine } {
  const byId = new Map(jobs.map((j) => [j.id, j]));
  const line = (r: CostRow): JobMarginLine => {
    const j = byId.get(r.jobId);
    return {
      id: r.jobId,
      number: r.jobNumber,
      customer: j?.customerName ?? null,
      summary: j?.summary ?? null,
      techs: j ? techsOf(j).join(', ') || '—' : null,
      revenueCents: r.revenueCents,
      costOfSalesCents: r.costOfSalesCents,
      labourCents: r.labourCents,
      labourMinutes: r.labourMinutes,
      grossProfitCents: r.grossProfitCents,
      marginPct: r.marginPct,
      notFinal: notFinalWhy(r).length > 0,
      unlogged: bare(r),
    };
  };
  return {
    rows: [...c.rows].sort((a, b) => b.grossProfitCents - a.grossProfitCents).map(line),
    total: {
      id: 'total',
      number: null,
      customer: null,
      summary: null,
      techs: null,
      revenueCents: c.revenueCents,
      costOfSalesCents: c.costOfSalesCents,
      labourCents: c.labourCents,
      labourMinutes: c.labourMinutes,
      grossProfitCents: c.grossProfitCents,
      marginPct: margin(c),
      notFinal: false,
      unlogged: false,
      total: true,
    },
  };
}

const UNDER: DataWord = { word: 'under target', tone: 'wait' };

/** The jobs' columns: the job (its way in), who was on it, its figures, its share and its numbers' state. */
export function jobMarginColumns(target: number | null, count: number): DataColumn<JobMarginLine>[] {
  return [
    {
      id: 'job',
      label: 'Job',
      rowHeader: true,
      way: true,
      cell: (r) =>
        r.total
          ? { text: `All ${count}` }
          : {
              text: r.number,
              lines: [
                ...(r.customer ? [{ text: r.customer }] : []),
                ...(r.summary ? [{ text: r.summary }] : []),
              ],
            },
    },
    { id: 'tech', label: 'Technician', cell: (r) => ({ text: r.techs ?? '' }) },
    { id: 'revenue', label: 'Revenue', align: 'end', cell: (r) => ({ text: money(r.revenueCents) }) },
    { id: 'costs', label: 'Costs', align: 'end', cell: (r) => ({ text: money(r.costOfSalesCents) }) },
    {
      id: 'labour',
      label: 'Labour',
      align: 'end',
      cell: (r) => ({
        text: money(r.labourCents),
        ...(r.labourMinutes > 0 ? { lines: [{ text: hoursText(r.labourMinutes) }] } : {}),
      }),
    },
    {
      id: 'gp',
      label: 'Gross profit',
      align: 'end',
      cell: (r) => ({
        text: money(r.grossProfitCents),
        ...(!r.total && r.grossProfitCents < 0 ? { look: 'bad' as const } : {}),
      }),
    },
    {
      id: 'share',
      label: 'Share',
      align: 'end',
      cell: (r) => ({
        text: marginText(r.marginPct),
        ...(underTarget(r.marginPct, target) ? { lines: [UNDER] } : {}),
      }),
    },
    {
      id: 'numbers',
      label: 'Numbers',
      cell: (r) => {
        if (r.total) return {};
        const words: DataWord[] = [
          ...(r.notFinal ? [{ word: 'Not final', tone: 'wait' as const }] : []),
          ...(r.unlogged ? [{ word: 'No materials', tone: 'wait' as const }] : []),
        ];
        if (!words.length) return { word: { word: 'Final', tone: 'muted' } };
        return { word: words[0]!, lines: words.slice(1) };
      },
    },
  ];
}

// ── By technician ──

export type TechLine = TechShare & { id: string };

/** The same jobs, technician by technician, by the time each logged: best per hour first. */
export const techLines = (rows: readonly CostRow[], jobs: readonly CostedJob[]): TechLine[] =>
  byTechnician(rows, jobs).map((t) => ({ ...t, id: t.name }));

/** Each technician's columns: their jobs and hours under the name, gross profit (and its share, under target marked), and per hour. */
export function techColumns(lines: readonly TechLine[], target: number | null): DataColumn<TechLine>[] {
  const most = Math.max(1, ...lines.map((r) => r.perHourCents ?? 0));
  return [
    // their jobs and hours under the name, so the three columns fit a side column
    {
      id: 'name',
      label: 'Technician',
      rowHeader: true,
      cell: (t) => ({
        text: t.name,
        lines: [{ text: `${plural(t.jobs, 'job')}, ${t.minutes ? hoursText(t.minutes) : 'no time logged'}` }],
      }),
    },
    {
      id: 'gp',
      label: 'Gross profit',
      align: 'end',
      cell: (t) => {
        const lines: (DataLine | DataWord)[] =
          t.marginPct === null
            ? []
            : [
                { text: `${marginText(t.marginPct)} of revenue` },
                ...(underTarget(t.marginPct, target) ? [UNDER] : []),
              ];
        return {
          text: money(t.grossProfitCents),
          ...(t.grossProfitCents < 0 ? { look: 'bad' as const } : {}),
          ...(lines.length ? { lines } : {}),
        };
      },
    },
    {
      id: 'hour',
      label: 'For each hour',
      cell: (t) =>
        t.perHourCents === null
          ? { text: 'no time logged', look: 'muted' }
          : {
              text: money(t.perHourCents),
              look: 'strong',
              meter: { value: Math.max(0, t.perHourCents), max: most, label: `${t.name} for each hour` },
            },
    },
  ];
}

// ── The huddle ──

const weekday = (date: string) =>
  new Intl.DateTimeFormat('en-CA', { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(`${date}T12:00:00Z`),
  );

/** The targets the office plans by, as a side column's rows. */
export function huddleTargets(p: HuddleDaysProps): KeyValueItem[] {
  return [
    {
      label: 'Technicians planned',
      value: p.targets.plannedTechs === null ? '—' : String(p.targets.plannedTechs),
    },
    {
      label: 'Calls a technician a day',
      value: p.targets.callsPerTechDay === null ? '—' : String(p.targets.callsPerTechDay),
    },
    {
      label: 'Conversion rate',
      value: p.budgeted.conversionPct === null ? '—' : `${p.budgeted.conversionPct} %`,
    },
    {
      label: 'Average ticket',
      value: p.budgeted.avgTicketCents === null ? '—' : money(p.budgeted.avgTicketCents),
    },
  ];
}

/**
 * The working days gone this month, newest first (the last `last`, or all of them with null): each one's
 * revenue as a bar against the best of them, hatched once the books closed it, and what it was made of.
 * `more` says there are days before the ones shown.
 */
export function huddleDayRows(p: HuddleDaysProps, last: number | null = 5): { rows: Row[]; more: boolean } {
  const gone = p.days.filter((d) => !d.future && (d.working || d.revenueCents)).reverse();
  const shown = last ? gone.slice(0, last) : gone;
  const most = Math.max(1, ...shown.map((d) => d.revenueCents));
  return {
    rows: shown.map((d) => ({
      id: d.date,
      name: `${d.today ? 'Today' : weekday(d.date)} ${Number(d.date.slice(8, 10))}`,
      bar: {
        value: d.revenueCents,
        max: most,
        label: `Revenue earned ${d.date}`,
        fill: d.closed ? 'hatch' : 'solid',
      },
      end: { figure: money(d.revenueCents) },
      lines: [
        {
          text: `${d.closed ? `${d.closed}. ` : ''}${plural(d.jobs, 'job')}, ${d.sold} sold${
            d.provisionalCents ? `, ${money(d.provisionalCents)} on draft invoices` : ''
          }${d.note ? `. ${d.note}` : ''}`,
        },
      ],
    })),
    more: last !== null && gone.length > shown.length,
  };
}
